import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { ConfigurationItem, DevicePort, NetworkDevice, PortNeighbor, ReadingGap } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageSection,
  SectionedPage,
  Spinner,
  StatusBadge,
  timeAgo,
} from '../components/common'
import { MibProdotto } from '../components/MibProdotto'
import { DataTable, type Column } from '../components/table'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * La rete vista **porta per porta**.
 *
 * ## Perché una sezione e non una colonna in più nell'elenco dei CI
 *
 * Un server si guarda come un oggetto: che sistema operativo ha, quanta
 * memoria, quale software. Uno switch no — uno switch *è* le sue porte, e la
 * domanda che ci si fa davanti a uno switch non è «che apparato è» ma «cosa c'è
 * attaccato alla 24, e perché è giù». Nell'elenco generale dei CI quella
 * domanda non ha posto: la riga di uno switch a quarantotto porte è lunga quanto
 * quella di una stampante.
 *
 * ## Perché la descrizione è la prima colonna dopo il nome
 *
 * `GigabitEthernet1/0/24` dice **dov'è** la porta sull'apparato, non a cosa
 * serve. Chi ha cablato quel dato l'ha già scritto — nella descrizione della
 * porta — e finora la sonda lo leggeva e lo buttava via. È il solo campo che
 * rende consultabile una tabella di quarantotto nomi identici a meno del numero
 * finale.
 */
const colonneApparati = (onVicini: (id: string) => void): Column<NetworkDevice>[] => [
  {
    key: 'nome',
    header: t('Nome'),
    value: (d) => d.name,
    render: (d) => (
      <Link to={`/cis/${d.id}`} onClick={(e) => e.stopPropagation()}>
        {d.name}
      </Link>
    ),
  },
  {
    key: 'classe',
    header: t('Classe'),
    value: (d) => d.class_label ?? t('non classificato'),
    render: (d) => (
      <>
        <StatusBadge status={d.class_label ?? t('non classificato')} />
        {d.class_confidence === 'low' && <span className="chip">{t('da verificare')}</span>}
      </>
    ),
  },
  { key: 'produttore', header: t('Produttore'), value: (d) => d.vendor ?? '' },
  { key: 'modello', header: t('Modello'), value: (d) => d.model ?? '' },
  {
    key: 'indirizzo',
    header: t('Indirizzo'),
    value: (d) => d.mgmt_ip ?? '',
    render: (d) => <span className="mono">{d.mgmt_ip ?? '—'}</span>,
  },
  {
    key: 'porte',
    header: t('Porte up'),
    type: 'number',
    align: 'right',
    // «0 porte» non è «tutte giù»: è la IF-MIB non letta. Si ordina sulle porte
    // attive, e la resa distingue i due casi con parole.
    value: (d) => (d.ports.total === 0 ? null : d.ports.up),
    render: (d) =>
      d.ports.total === 0 ? (
        <span className="text-muted">{t('non lette')}</span>
      ) : (
        <>
          {d.ports.up}/{d.ports.total}
          {d.ports.admin_down > 0 && (
            <span className="chip" title={t('Spente da un amministratore')}>
              {t('{n} disabilitate', { n: d.ports.admin_down })}
            </span>
          )}
        </>
      ),
  },
  {
    key: 'vicini',
    header: t('Vicini'),
    type: 'number',
    align: 'right',
    value: (d) => d.neighbors,
    // The number opens the device's ports with a neighbour only.
    render: (d) => (d.neighbors > 0 ? (
      <button type="button" className="linklike" title={t('Mostra le porte con un vicino')}
              onClick={(e) => { e.stopPropagation(); onVicini(d.id) }}>
        {d.neighbors}
      </button>
    ) : d.neighbors),
  },
  {
    key: 'backup',
    header: t('Backup'),
    value: (d) => (d.has_config ? t('sì') : t('no')),
    render: (d) =>
      d.has_config ? (
        <span className="chip chip--ok">{t('sì')}</span>
      ) : (
        <span className="text-muted">{t('no')}</span>
      ),
  },
  {
    key: 'visto',
    header: t('Visto'),
    type: 'date',
    value: (d) => d.last_seen,
    render: (d) => <span title={d.last_seen ?? ''}>{timeAgo(d.last_seen)}</span>,
  },
]

export default function NetworkDevices() {
  const [params, setParams] = useSearchParams()
  const selected = params.get('device') ?? ''
  const devices = useAsync(() => api.networkDevices(), [])

  const select = (id: string, soloVicini = false) => {
    const next = new URLSearchParams(params)
    if (id) next.set('device', id)
    else next.delete('device')
    if (soloVicini) next.set('neighbors', '1')
    else next.delete('neighbors')
    setParams(next)
    if (id) {
      // After the panel renders: bring the ports into view.
      setTimeout(() => document.getElementById('porte-apparato')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    }
  }

  const rows = devices.data ?? []

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Rete')}</h1>
          <p className="page__subtitle">
            {t('Apparati di rete, porte e collegamenti')}
          </p>
        </div>
        <button className="secondary" onClick={() => devices.reload()}>
          {t('Aggiorna')}
        </button>
      </header>

      <ErrorBanner error={devices.error} />

      <SectionedPage
        sections={[
          { id: 'apparati', label: t('Apparati ({n})', { n: rows.length }) },
          { id: 'componenti', label: t('Componenti') },
          { id: 'ip', label: t('Indirizzi IP') },
        ]}
      >
        <PageSection id="apparati">
          <Card title={t('Apparati ({n})', { n: rows.length })}>
            {devices.loading && !devices.data ? (
              <Spinner />
            ) : rows.length === 0 ? (
              <EmptyState
                title={t('Nessun apparato di rete')}
                hint={t('Si popola con una discovery SNMP. Compaiono anche gli apparati non ancora classificati: sono quelli che più spesso si vogliono guardare.')}
              />
            ) : (
              <DataTable
                rows={rows}
                columns={colonneApparati((id) => select(id, true))}
                rowKey={(d) => d.id}
                onRowClick={(d) => select(d.id === selected ? '' : d.id)}
                isSelected={(d) => d.id === selected}
                initialSort={{ key: 'nome' }}
                sourceNote={t("Seleziona una riga per vedere le porte dell'apparato.")}
              />
            )}
          </Card>

          {/* The ports of the chosen device right under the table, scrolled
              into view: below the coverage card they looked like nothing had
              opened (field report, 02/10/2026). */}
          {selected && (
            <div id="porte-apparato">
              <Ports ciId={selected} soloVicini={params.get('neighbors') === '1'}
                     onTutte={() => select(selected)} />
            </div>
          )}

          <Coverage />

          <MibProdotto />
        </PageSection>

        <PageSection id="componenti">
          <Componenti />
        </PageSection>

        <PageSection id="ip">
          <IndirizziIp />
        </PageSection>
      </SectionedPage>
    </>
  )
}

const colonneComponenti = (): Column<ConfigurationItem>[] => [
  {
    key: 'nome',
    header: t('Nome'),
    value: (c) => c.name ?? '',
    render: (c) => <Link to={`/cis/${c.id}`}>{c.name ?? c.ci_uid ?? '—'}</Link>,
  },
  { key: 'classe', header: t('Tipo'), value: (c) => c.ci_class ?? '' },
  { key: 'produttore', header: t('Produttore'), value: (c) => c.vendor ?? '' },
  {
    key: 'modello',
    header: t('Modello'),
    value: (c) => String((c.attributes as Record<string, unknown>)?.model ?? ''),
  },
  {
    key: 'seriale',
    header: 'Serial Number',
    value: (c) => String((c.attributes as Record<string, unknown>)?.serial ?? ''),
    render: (c) => (
      <span className="mono">
        {String((c.attributes as Record<string, unknown>)?.serial ?? '—')}
      </span>
    ),
  },
  {
    key: 'visto',
    header: t('Visto'),
    type: 'date',
    value: (c) => c.last_seen,
    render: (c) => <span title={c.last_seen ?? ''}>{timeAgo(c.last_seen)}</span>,
  },
]

/**
 * I **componenti**: le parti dentro gli apparati — transceiver (SFP), schede di
 * linea, alimentatori, ventole, moduli. Su uno switch a telaio sono ciò che si
 * guasta e si sostituisce, e finché stavano solo dentro la scheda del singolo
 * apparato non si potevano contare né cercare per seriale in un posto solo.
 */
function Componenti() {
  const parti = useAsync(() => api.cis({ category: 'part', limit: 500 }), [])
  const rows = parti.data ?? []
  return (
    <Card title={t('Componenti ({n})', { n: rows.length })}>
      {parti.loading && !parti.data ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('Nessun componente')}
          hint={t('Transceiver, schede, alimentatori e ventole compaiono qui quando una scansione SNMP legge le parti inserite negli apparati.')}
        />
      ) : (
        <DataTable rows={rows} columns={colonneComponenti()} rowKey={(c) => c.id} initialSort={{ key: 'nome' }} />
      )}
    </Card>
  )
}

const colonneIp = (): Column<ConfigurationItem>[] => [
  {
    key: 'indirizzo',
    header: t('Indirizzo'),
    value: (c) => String((c.attributes as Record<string, unknown>)?.mgmt_ip ?? c.name ?? ''),
    render: (c) => (
      <Link to={`/cis/${c.id}`} className="mono">
        {String((c.attributes as Record<string, unknown>)?.mgmt_ip ?? c.name ?? '—')}
      </Link>
    ),
  },
  {
    key: 'tipo',
    header: t('Tipo'),
    value: (c) => (c.ci_class === 'public_ip' ? t('IP pubblico') : t('Indirizzo aggiuntivo')),
  },
  {
    key: 'visto',
    header: t('Visto'),
    type: 'date',
    value: (c) => c.last_seen,
    render: (c) => <span title={c.last_seen ?? ''}>{timeAgo(c.last_seen)}</span>,
  },
]

/**
 * Gli **indirizzi IP** come oggetti a sé: gli indirizzi aggiuntivi (VIP, alias,
 * IP di interfaccia degli apparati) e gli IP pubblici. Sono ciò che, senza una
 * vista dedicata, si ritrova come «IP zombie» — un indirizzo nel traffico che
 * nessuno sa a chi appartenga. Qui si vedono tutti, con il tipo e da quando.
 */
function IndirizziIp() {
  const aggiuntivi = useAsync(() => api.cis({ ci_class: 'network_address', limit: 500 }), [])
  const pubblici = useAsync(() => api.cis({ ci_class: 'public_ip', limit: 500 }), [])
  const rows = [...(aggiuntivi.data ?? []), ...(pubblici.data ?? [])]
  const caricando = (aggiuntivi.loading && !aggiuntivi.data) || (pubblici.loading && !pubblici.data)
  return (
    <Card title={t('Indirizzi IP ({n})', { n: rows.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('«Indirizzo aggiuntivo»: un indirizzo di un apparato oltre quello di gestione, come le interfacce VLAN, le loopback e i VIP. «IP pubblico»: un indirizzo con cui un asset è raggiungibile da Internet.')}
      </p>
      {caricando ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('Nessun indirizzo')}
          hint={t('Gli indirizzi aggiuntivi e pubblici compaiono qui man mano che le scansioni li rilevano sugli apparati e sugli host.')}
        />
      ) : (
        <DataTable rows={rows} columns={colonneIp()} rowKey={(c) => c.id} initialSort={{ key: 'indirizzo' }} />
      )}
    </Card>
  )
}

/**
 * Che cosa non si sa ancora leggere, e su quanti apparati.
 *
 * ## Perché una tabella dei buchi
 *
 * Nessuna tabella di regole copre ciò che non si è ancora visto: un cliente
 * nuovo porta apparati nuovi, e non è un difetto da eliminare — è la condizione
 * normale. Ciò che si può garantire è che il non coperto **si veda**.
 *
 * Senza, l'unico modo di accorgersi di un apparato letto a metà è che qualcuno
 * scorra l'elenco e noti un trattino, una riga per volta. Qui il problema
 * smette di essere aperto e diventa una lista di lavoro finita, ordinata per
 * quanti apparati ciascuna regola mancante coprirebbe.
 */
function Coverage() {
  const gaps = useAsync(() => api.readingCoverage(), [])
  const rows = gaps.data ?? []
  if (gaps.loading && !gaps.data) return null
  if (rows.length === 0) return null

  return (
    <Card title={t('Da riconoscere ({n})', { n: rows.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Apparati raggiungibili e presenti nel CMDB di cui non tutte le informazioni necessarie sono state rilevate. Gli apparati della stessa serie sono raggruppati in una riga.')}
      </p>
      {/*
        «Nessun MIB importato» e «MIB importati, e questo apparato non c'è» sono
        due situazioni diverse che portano ad azioni diverse. Senza il numero la
        prima si legge come la seconda, e si va a cercare un MIB che si ha già.
      */}
      {/* Il registro dei costruttori è una domanda **diversa** dai MIB: «di chi
          è» invece di «che modello è». Senza distinguerle, chi non ha importato
          nessuno dei due va a cercare i MIB — che sono la parte difficile, da
          chiedere al fornitore — mentre il produttore gli arriverebbe da un file
          pubblico scaricato una volta. */}
      {rows[0].enterprises_size === 0 && (
        <div className="banner banner--info">
          {t('Registro dei costruttori non caricato: il produttore si ricava solo per le marche più diffuse. Di norma arriva con l’immagine; se manca, si carica con un pulsante dalla console di amministrazione, in «Piattaforma → Registro dei costruttori». Da lì il produttore c’è per qualunque apparato SNMP, anche di marche mai viste.')}
        </div>
      )}
      {rows[0].registry_size === 0 ? (
        <div className="banner banner--info">
          {t('Nessun MIB di prodotto importato: il sistema non può ricavare il modello dall’OID che l’apparato dichiara in {campo}. Scaricali dal portale di supporto del costruttore e caricali qui sotto, in «MIB di prodotto».', { campo: 'sysObjectID' })}
        </div>
      ) : (
        <p className="field__hint">
          {t('{n} registrazioni di prodotto caricate dai MIB. Le righe qui sotto con un OID e senza corrispondenza sono quelle per cui manca il MIB di quel costruttore.', { n: rows[0].registry_size.toLocaleString(localeCorrente()) })}
        </p>
      )}
      <DataTable
        rows={rows}
        columns={colonneCopertura()}
        rowKey={(g) => g.signature}
        initialSort={{ key: 'count', dir: 'desc' }}
        conteggio={{ nome: t('gruppi') }}
      />
    </Card>
  )
}

/** Le porte dell'apparato scelto, con il vicino su ciascuna. */
function Ports({ ciId, soloVicini = false, onTutte }: {
  ciId: string
  /** Only the ports with a neighbour (opened from the «Vicini» number). */
  soloVicini?: boolean
  onTutte?: () => void
}) {
  const ports = useAsync(() => api.devicePorts(ciId), [ciId])
  // Le porte spente sono la maggioranza su uno switch di accesso, e non sono
  // quello che si sta cercando: si nascondono per difetto, ma il conteggio resta
  // visibile — nasconderle *e* non dirlo farebbe sembrare più piccolo
  // l'apparato.
  const [showAll, setShowAll] = useState(false)

  if (ports.error) return <ErrorBanner error={ports.error} />
  if (ports.loading && !ports.data) return <Spinner />
  if (!ports.data) return null

  const { name, health, unmatched } = ports.data
  const interesting = (p: DevicePort) =>
    p.oper_status !== 'down' || p.description || p.neighbors.length > 0
  const shown = soloVicini
    ? ports.data.ports.filter((p) => p.neighbors.length > 0)
    : showAll ? ports.data.ports : ports.data.ports.filter(interesting)
  const hidden = ports.data.ports.length - shown.length

  return (
    <Card title={soloVicini
      ? t('Porte di {nome} con un vicino', { nome: name })
      : t('Porte di {nome} ({su}/{totale} up)', { nome: name, su: health.up, totale: health.total })}>
      {ports.data.ports.length === 0 ? (
        <EmptyState
          title={t('Nessuna porta raccolta')}
          hint={t('Le porte arrivano dalla IF-MIB: uno sweep SNMP non le legge, serve una discovery approfondita.')}
        />
      ) : (
        <>
          <DataTable rows={shown} columns={colonnePorte()} rowKey={(p) => p.id} conteggio={{ nome: t('porte') }} />

          {soloVicini && onTutte && (
            <button className="secondary" style={{ marginTop: 12 }} onClick={onTutte}>
              {t('Mostra tutte le porte')}
            </button>
          )}
          {!soloVicini && hidden > 0 && (
            <button className="secondary" style={{ marginTop: 12 }} onClick={() => setShowAll(true)}>
              {t('Mostra anche le {n} porte spente e senza descrizione', { n: hidden })}
            </button>
          )}
        </>
      )}

      {/*
        Un vicino dichiarato su una porta che non si ritrova nella IF-MIB non
        si butta. Le adiacenze raccolte e quelle mostrate devono essere lo
        stesso numero: un vicino che sparisce è un difetto che nessuno nota,
        perché la tabella sembra completa.
      */}
      {unmatched.length > 0 && (
        <>
          <p className="field__hint" style={{ marginTop: 16 }}>
            {t('Questi vicini sono stati raccolti, ma la porta che dichiarano non corrisponde a nessuna interfaccia letta: succede quando CDP o LLDP usano una convenzione di nomi che non si riconosce, o quando la IF-MIB non è stata raccolta.')}
          </p>
          <DataTable
            rows={unmatched}
            columns={colonneNonAbbinati()}
            rowKey={(_n, i) => String(i)}
            conteggio={{ nome: t('vicini') }}
          />
        </>
      )}
    </Card>
  )
}

/**
 * Lo stato di una porta, che sono due stati.
 *
 * Una porta spenta da un amministratore e una caduta da sola sono situazioni
 * che portano ad azioni opposte: la prima è una decisione, la seconda un
 * guasto. Mostrarne uno solo — di solito quello operativo — le fa sembrare la
 * stessa cosa.
 */
function testoStatoPorta(p: DevicePort): string | null {
  if (!p.oper_status && !p.admin_status) return null
  if (p.admin_status === 'down') return t('disabilitata')
  if (p.oper_status === 'up') return t('su')
  if (p.oper_status === 'lower_layer_down') return t('giù (livello inferiore)')
  return p.oper_status ?? p.admin_status ?? '—'
}

function portStatus(p: DevicePort) {
  const stato = testoStatoPorta(p)
  if (stato === null) return <span className="text-muted">—</span>
  return <StatusBadge status={stato} />
}

/** The «Da riconoscere» table: one row per series of devices read only in part. */
const colonneCopertura = (): Column<ReadingGap>[] => {
  const meno = <span className="text-muted">—</span>
  const mancante = (m: string) => (m === 'model' ? t('modello') : m === 'software_rev' ? t('versione') : m)
  return [
    { key: 'count', header: t('Apparati'), type: 'number' },
    {
      key: 'missing',
      header: t('Manca'),
      value: (g) => g.missing.map(mancante).join(' '),
      render: (g) =>
        g.missing.map((m) => (
          <span key={m} className="chip">
            {mancante(m)}
          </span>
        )),
    },
    {
      // La descrizione completa e non la forma: è quella che serve a chi deve
      // scrivere la regola, e senza si segnala un problema senza dare da lavorare.
      key: 'example',
      header: t('Descrizione'),
      render: (g) => <span title={g.example}>{g.example || meno}</span>,
    },
    { key: 'sys_object_id', header: t('OID costruttore'), mono: true },
    {
      key: 'known_vendor',
      header: t('Costruttore'),
      value: (g) => (!g.sys_object_id ? '' : g.known_vendor ? t('noto') : t('ignoto')),
      render: (g) =>
        !g.sys_object_id ? (
          meno
        ) : g.known_vendor ? (
          t('noto')
        ) : (
          <span className="chip" title={t('Manca dal registro degli enti: importalo')}>
            {t('ignoto')}
          </span>
        ),
    },
    {
      key: 'known_oid',
      header: t('Nei MIB'),
      value: (g) => (!g.sys_object_id ? '' : g.known_oid ? t('sì') : t('manca')),
      render: (g) =>
        !g.sys_object_id ? (
          meno
        ) : g.known_oid ? (
          t('sì')
        ) : (
          <span className="chip" title={t('Serve il MIB di prodotto di questo costruttore')}>
            {t('manca')}
          </span>
        ),
    },
  ]
}

/** A neighbour's name, linked to its CI when there is one. */
function vicino(n: PortNeighbor) {
  return n.peer_id ? <Link to={`/cis/${n.peer_id}`}>{n.peer_name}</Link> : (n.peer_name ?? '—')
}

/** The ports of a device, with what each one has in front. */
const colonnePorte = (): Column<DevicePort>[] => [
  { key: 'if_name', header: t('Porta'), mono: true, value: (p) => p.if_name ?? p.if_index },
  {
    key: 'description',
    header: t('Descrizione'),
    render: (p) => p.description || <span className="text-muted">—</span>,
  },
  { key: 'stato', header: t('Stato'), value: testoStatoPorta, render: portStatus },
  {
    key: 'speed_bps',
    header: t('Velocità'),
    value: (p) => p.speed_bps || null,
    render: (p) => formatSpeed(p.speed_bps),
  },
  {
    key: 'neighbors',
    header: t('Collegato a'),
    value: (p) => p.neighbors.map((n) => n.peer_name ?? '').filter(Boolean).join(', '),
    render: (p) =>
      p.neighbors.length === 0
        ? '—'
        : p.neighbors.map((n, i) => (
            <span key={i}>
              {i > 0 && ', '}
              {vicino(n)}
            </span>
          )),
  },
  {
    key: 'remote_port',
    header: t('Porta remota'),
    mono: true,
    value: (p) => p.neighbors.map((n) => n.remote_port).filter(Boolean).join(', '),
  },
  { key: 'mac', header: 'MAC', mono: true },
  { key: 'ip', header: 'IP', mono: true },
]

/** Neighbours declared on a port that no interface matches. */
const colonneNonAbbinati = (): Column<PortNeighbor>[] => [
  { key: 'local_port', header: t('Porta dichiarata'), mono: true },
  { key: 'peer_name', header: t('Collegato a'), render: vicino },
  { key: 'remote_port', header: t('Porta remota'), mono: true },
  { key: 'via', header: t('Protocollo'), mono: true, value: (n) => (n.via ?? '—').toUpperCase() },
]

/** Velocità nominale in una forma che si legge. */
function formatSpeed(bps: number | null): string {
  if (!bps) return '—'
  if (bps >= 1_000_000_000) return `${Math.round(bps / 1_000_000_000)} Gbit/s`
  if (bps >= 1_000_000) return `${Math.round(bps / 1_000_000)} Mbit/s`
  return `${bps} bit/s`
}
